import React, { useEffect, useMemo, useState } from 'react'
import {
    Card,
    Row,
    Col,
    Select,
    DatePicker,
    InputNumber,
    Input,
    Button,
    Radio,
    Table,
    Progress,
    Collapse,
    Tag,
    Typography,
    Space,
    Divider,
    message
} from 'antd'
import { SearchOutlined, SwapRightOutlined } from '@ant-design/icons'
import moment from 'moment'
import _ from 'lodash'
import { Scatter } from 'react-chartjs-2'
import * as api from '../api'

const { Option } = Select
const { Panel } = Collapse
const { Text, Title } = Typography

const CABINS = ['Economy', 'Premium Economy', 'Business', 'First']
const PALETTE = [
    '#1f97a8',
    '#c98a39',
    '#8a7ff0',
    '#5ab86b',
    '#e0503a',
    '#d9a441',
    '#39b7c9',
    '#b86bb8'
]

// --- persist the form to the URL hash query so F5 keeps everything ---
function parseParams() {
    try {
        const hash = window.location.hash || ''
        const qi = hash.indexOf('?')
        if (qi < 0) return {}
        const sp = new URLSearchParams(hash.slice(qi + 1))
        const num = v => (v != null && v !== '' ? Number(v) : undefined)
        return {
            user: num(sp.get('user')),
            suppliers: (sp.get('suppliers') || '').split(',').filter(Boolean).map(Number),
            from: sp.get('from') || undefined,
            to: sp.get('to') || undefined,
            round: sp.get('round') === '1' ? true : sp.get('round') === '0' ? false : undefined,
            depart: sp.get('depart') || undefined,
            ret: sp.get('ret') || undefined,
            adults: num(sp.get('adults')),
            children: num(sp.get('children')),
            infants: num(sp.get('infants')),
            cabin: sp.get('cabin') || undefined,
            cur: sp.get('cur') || undefined,
            wait: num(sp.get('wait'))
        }
    } catch (e) {
        return {}
    }
}

function writeParams(s) {
    try {
        const sp = new URLSearchParams()
        const set = (k, v) => {
            if (v == null || v === '' || (Array.isArray(v) && v.length === 0)) return
            sp.set(k, Array.isArray(v) ? v.join(',') : `${v}`)
        }
        set('user', s.userId)
        set('suppliers', s.supplierIds)
        set('from', s.fromCode)
        set('to', s.toCode)
        set('round', s.roundTrip ? '1' : '0')
        if (s.departDate) set('depart', s.departDate.format('YYYY-MM-DD'))
        if (s.roundTrip && s.returnDate) set('ret', s.returnDate.format('YYYY-MM-DD'))
        set('adults', s.adults)
        set('children', s.children)
        set('infants', s.infants)
        set('cabin', s.cabin)
        set('cur', s.currency)
        set('wait', s.waitTime)
        const hash = window.location.hash || '#/'
        const path = hash.replace(/^#/, '').split('?')[0]
        window.history.replaceState(null, '', `#${path}?${sp.toString()}`)
    } catch (e) {
        /* ignore */
    }
}

function fareOf(f) {
    const fare = (f.fares && f.fares[0]) || {}
    return { total: Number(fare.total_fare), currency: fare.currency || '' }
}

// flight_no already carries the carrier prefix (e.g. "VZ680") — don't prepend airline_code
function legInfo(legs) {
    if (!legs || !legs.length) return null
    const first = legs[0]
    const last = legs[legs.length - 1]
    const fmt = dt => (dt ? moment(dt).format('DD MMM HH:mm') : '')
    // full path incl. connection points: KUL→AUH→LHR
    const path = [
        first.departure_airport_code,
        ...legs.map(l => l.arrival_airport_code)
    ].filter(Boolean)
    return {
        nos: legs
            .map(l => l.flight_no || `${l.airline_code || ''}${l.origin_flight_no || ''}`)
            .join(' › '),
        route: path.join('→'),
        dep: fmt(first.departure_date_time),
        arr: fmt(last.arrival_date_time),
        stops: legs.length - 1
    }
}

function LegCell({ legs }) {
    const info = legInfo(legs)
    if (!info) return <Text type="secondary">—</Text>
    return (
        <div style={{ lineHeight: 1.4 }}>
            <div>
                <Text strong>{info.nos}</Text>{' '}
                <Text type="secondary">{info.route}</Text>
                {info.stops > 0 && (
                    <Tag style={{ marginLeft: 6 }} color="orange">
                        {info.stops} stop{info.stops > 1 ? 's' : ''}
                    </Tag>
                )}
            </div>
            <Text type="secondary" style={{ fontSize: 12 }}>
                {info.dep} → {info.arr}
            </Text>
        </div>
    )
}

function SupplierPriceChart({ flights }) {
    const stats = useMemo(() => {
        const bySup = _.groupBy(flights, 'supplier_code')
        return Object.entries(bySup)
            .map(([sup, list], i) => {
                const prices = list.map(f => fareOf(f).total).filter(p => !Number.isNaN(p))
                return {
                    sup,
                    color: PALETTE[i % PALETTE.length],
                    n: prices.length,
                    min: _.min(prices),
                    max: _.max(prices),
                    avg: _.mean(prices),
                    prices
                }
            })
            .sort((a, b) => a.min - b.min)
    }, [flights])

    if (!stats.length) return null
    const currency = fareOf(flights[0]).currency

    // one dot per fare, one row per supplier — clusters and outliers pop out
    const data = {
        datasets: stats.map((s, i) => ({
            label: `${s.sup} (${s.n})`,
            data: s.prices.map(p => ({ x: p, y: i })),
            backgroundColor: `${s.color}bb`,
            borderColor: s.color,
            pointRadius: 3.5,
            pointHoverRadius: 6
        }))
    }
    const options = {
        legend: { display: false },
        maintainAspectRatio: false,
        tooltips: {
            callbacks: {
                title: item =>
                    item && item[0] ? stats[item[0].datasetIndex].sup : '',
                label: item => {
                    const s = stats[item.datasetIndex]
                    return ` ${Number(item.xLabel).toLocaleString()} ${currency}  (min ${s.min.toLocaleString()} · avg ${Math.round(s.avg).toLocaleString()} · max ${s.max.toLocaleString()})`
                }
            }
        },
        scales: {
            xAxes: [
                {
                    ticks: { callback: v => v.toLocaleString() },
                    scaleLabel: { display: true, labelString: `total fare (${currency})` },
                    gridLines: { color: 'rgba(0,0,0,0.05)' }
                }
            ],
            yAxes: [
                {
                    ticks: {
                        min: -0.5,
                        max: stats.length - 0.5,
                        stepSize: 1,
                        callback: v =>
                            Number.isInteger(v) && v >= 0 && v < stats.length
                                ? `${stats[v].sup} (${stats[v].n})`
                                : ''
                    },
                    gridLines: { color: 'rgba(0,0,0,0.05)' }
                }
            ]
        }
    }

    return (
        <Collapse defaultActiveKey={['dist']} ghost style={{ marginBottom: 8 }}>
            <Collapse.Panel header="Price distribution by supplier" key="dist">
                <div style={{ height: Math.max(140, stats.length * 44 + 70) }}>
                    <Scatter data={data} options={options} />
                </div>
            </Collapse.Panel>
        </Collapse>
    )
}

export default function FlightReproduce() {
    const init = useMemo(parseParams, [])

    const [users, setUsers] = useState([])
    const [loadingUsers, setLoadingUsers] = useState(false)
    const [userId, setUserId] = useState(init.user)
    const [suppliers, setSuppliers] = useState([])
    const [loadingSuppliers, setLoadingSuppliers] = useState(false)
    const [supplierIds, setSupplierIds] = useState(init.suppliers || [])

    const [fromOpts, setFromOpts] = useState(
        init.from ? [{ iata_code: init.from, name: init.from }] : []
    )
    const [toOpts, setToOpts] = useState(init.to ? [{ iata_code: init.to, name: init.to }] : [])
    const [fromCode, setFromCode] = useState(init.from)
    const [toCode, setToCode] = useState(init.to)

    const [roundTrip, setRoundTrip] = useState(init.round || false)
    const [departDate, setDepartDate] = useState(
        init.depart ? moment(init.depart) : moment().add(30, 'day')
    )
    const [returnDate, setReturnDate] = useState(
        init.ret ? moment(init.ret) : moment().add(35, 'day')
    )
    const [adults, setAdults] = useState(init.adults || 1)
    const [children, setChildren] = useState(init.children || 0)
    const [infants, setInfants] = useState(init.infants || 0)
    const [cabin, setCabin] = useState(init.cabin || 'Economy')
    const [currency, setCurrency] = useState(init.cur || '')
    const [waitTime, setWaitTime] = useState(init.wait || 45000)

    const [running, setRunning] = useState(false)
    const [progress, setProgress] = useState(0)
    const [result, setResult] = useState(null)
    const [builtReq, setBuiltReq] = useState(null)
    const [curl, setCurl] = useState('')

    useEffect(() => {
        setLoadingUsers(true)
        api.getUsers()
            .then(setUsers)
            .catch(e => message.error(`Load users failed: ${e.message}`))
            .finally(() => setLoadingUsers(false))
        if (init.user) {
            setLoadingSuppliers(true)
            api.getUserSuppliers(init.user)
                .then(setSuppliers)
                .catch(() => {})
                .finally(() => setLoadingSuppliers(false))
        }
    }, [])

    // keep the URL hash query in sync so F5 restores the form
    useEffect(() => {
        writeParams({
            userId,
            supplierIds,
            fromCode,
            toCode,
            roundTrip,
            departDate,
            returnDate,
            adults,
            children,
            infants,
            cabin,
            currency,
            waitTime
        })
    }, [
        userId,
        supplierIds,
        fromCode,
        toCode,
        roundTrip,
        departDate,
        returnDate,
        adults,
        children,
        infants,
        cabin,
        currency,
        waitTime
    ])

    const onUserChange = uid => {
        setUserId(uid)
        setSupplierIds([])
        setSuppliers([])
        if (!uid) return
        setLoadingSuppliers(true)
        api.getUserSuppliers(uid)
            .then(setSuppliers)
            .catch(e => message.error(`Load suppliers failed: ${e.message}`))
            .finally(() => setLoadingSuppliers(false))
    }

    const searchFrom = useMemo(
        () =>
            _.debounce(q => {
                if (!q || q.length < 2) return
                api.searchAirports(q).then(setFromOpts).catch(() => setFromOpts([]))
            }, 350),
        []
    )
    const searchTo = useMemo(
        () =>
            _.debounce(q => {
                if (!q || q.length < 2) return
                api.searchAirports(q).then(setToOpts).catch(() => setToOpts([]))
            }, 350),
        []
    )

    // trace-id → prefill the form from a real prod flight search
    const [traceInput, setTraceInput] = useState('')
    const [tracing, setTracing] = useState(false)
    const fillFromTrace = async () => {
        if (!traceInput.trim()) return message.warning('Paste a trace-id or a URL containing one')
        setTracing(true)
        try {
            const { payload, email } = await api.traceToPayload(traceInput)
            if (email) {
                const u = users.find(x => x.email === email)
                if (u) onUserChange(u.id)
                else message.warning(`User ${email} not in the list — pick one manually`)
            }
            if (payload.departure_airport_code) {
                setFromOpts([{ iata_code: payload.departure_airport_code, name: '' }])
                setFromCode(payload.departure_airport_code)
            }
            if (payload.arrival_airport_code) {
                setToOpts([{ iata_code: payload.arrival_airport_code, name: '' }])
                setToCode(payload.arrival_airport_code)
            }
            setRoundTrip(!!payload.is_round_trip)
            if (payload.departure_date) setDepartDate(moment(payload.departure_date))
            if (payload.return_date) setReturnDate(moment(payload.return_date))
            if (payload.adults) setAdults(payload.adults)
            setChildren(payload.children || 0)
            setInfants(payload.infants || 0)
            if (payload.cabin_class) setCabin(payload.cabin_class)
            if (payload.currency) setCurrency(String(payload.currency).toUpperCase())
            message.success('Form filled from trace — review and Run')
        } catch (e) {
            message.error(
                `Trace fill failed: ${_.get(e, 'response.data.message') || e.message}`,
                10
            )
        } finally {
            setTracing(false)
        }
    }

    const run = async () => {
        if (!userId) return message.warning('Pick a user/switch')
        if (!fromCode || !toCode) return message.warning('Pick From/To airports')
        if (!departDate) return message.warning('Pick a departure date')
        if (roundTrip && !returnDate) return message.warning('Pick a return date')

        setRunning(true)
        setProgress(0)
        setResult(null)
        try {
            const user = users.find(u => u.id === userId)
            const token = await api.genToken(user.id, user.email)

            const body = {
                departure_airport_code: fromCode,
                arrival_airport_code: toCode,
                is_round_trip: roundTrip,
                departure_date: departDate.format('YYYY-MM-DD'),
                adults,
                children,
                infants,
                cabin_class: cabin,
                wait_time: waitTime
            }
            if (roundTrip) body.return_date = returnDate.format('YYYY-MM-DD')
            // short per-poll server wait → progressive results; total budget = waitTime
            body.wait_time = Math.min(8000, waitTime)
            if (currency) body.currency = currency.toUpperCase()
            if (supplierIds.length) body.supplier_ids = supplierIds

            setBuiltReq(body)
            const headers = {
                'Content-Type': 'application/json',
                'x-key': user.email,
                'x-access-token': token
            }
            setCurl(
                `curl -X POST '${api.endpoints.FLIGHT}/get/flights' \\\n  -H 'Content-Type: application/json' \\\n  -H 'x-key: ${user.email}' \\\n  -H 'x-access-token: ${token}' \\\n  -d '${JSON.stringify(body)}'`
            )

            const res = await api.runSearch({
                headers,
                body,
                maxWaitMs: Math.max(waitTime + 30000, 90000),
                // progressive: render flights as suppliers come back
                onTick: ({ progress: p, res: tick }) => {
                    setProgress(Math.round((p || 0) * 100))
                    if (tick && Array.isArray(tick.data)) setResult(tick)
                }
            })
            setProgress(100)
            setResult(res)
            message.success(
                `Done — ${(res.data || []).length} flight(s), progress ${Math.round(
                    (res.progress || 0) * 100
                )}%`
            )
        } catch (e) {
            const msg = _.get(e, 'response.data.message') || e.message
            message.error(`Search failed: ${msg}`, 12)
            setResult(_.get(e, 'response.data') || { error: msg })
        } finally {
            setRunning(false)
        }
    }

    const flights = (result && result.data) || []

    const columns = [
        {
            title: 'Supplier',
            dataIndex: 'supplier_code',
            width: 100,
            filters: _.uniq(flights.map(f => f.supplier_code)).map(s => ({
                text: s,
                value: s
            })),
            onFilter: (v, r) => r.supplier_code === v,
            render: c => <Tag>{c}</Tag>
        },
        {
            title: 'Outbound',
            render: f => <LegCell legs={f.outbound} />
        },
        {
            title: 'Inbound',
            render: f => <LegCell legs={f.inbound} />
        },
        {
            title: 'Class',
            width: 110,
            render: f => {
                const fare = (f.fares && f.fares[0]) || {}
                return `${fare.cabin_class || ''} ${fare.booking_class ? `(${fare.booking_class})` : ''}`
            }
        },
        {
            title: 'Total fare',
            width: 140,
            sorter: (a, b) => fareOf(a).total - fareOf(b).total,
            defaultSortOrder: 'ascend',
            render: f => {
                const { total, currency: cur } = fareOf(f)
                return (
                    <Text strong>
                        {Number.isNaN(total) ? '—' : total.toLocaleString()} {cur}
                    </Text>
                )
            }
        }
    ]

    return (
        <div style={{ padding: 16 }}>
            <Title level={4}>Flight Search Reproduce</Title>
            <Text type="secondary">
                Reproduce a flight search on flight-api — pick a switch, suppliers, route, dates and
                pax, then fire /get/flights.
            </Text>

            <Card size="small" style={{ marginTop: 16 }}>
                <Row gutter={8} style={{ marginBottom: 12 }}>
                    <Col flex="auto">
                        <Input
                            placeholder="Prefill from a prod search: paste a trace-id (or any URL/text containing it)"
                            value={traceInput}
                            onChange={e => setTraceInput(e.target.value)}
                            onPressEnter={fillFromTrace}
                            allowClear
                        />
                    </Col>
                    <Col>
                        <Button loading={tracing} onClick={fillFromTrace}>
                            Fill from trace
                        </Button>
                    </Col>
                </Row>
                <Row gutter={[16, 16]}>
                    <Col xs={24} md={12}>
                        <label>User / switch</label>
                        <Select
                            style={{ width: '100%' }}
                            placeholder="Select a switch"
                            loading={loadingUsers}
                            showSearch
                            optionFilterProp="children"
                            value={userId}
                            onChange={onUserChange}
                        >
                            {users.map(u => (
                                <Option key={u.id} value={u.id}>
                                    {(u.name || u.email) + ' — ' + u.email}
                                </Option>
                            ))}
                        </Select>
                    </Col>
                    <Col xs={24} md={12}>
                        <label>Suppliers (empty = all enabled)</label>
                        <Select
                            mode="multiple"
                            style={{ width: '100%' }}
                            placeholder={userId ? 'All enabled suppliers' : 'Pick a user first'}
                            loading={loadingSuppliers}
                            disabled={!userId}
                            value={supplierIds}
                            onChange={setSupplierIds}
                            optionFilterProp="children"
                        >
                            {suppliers.map(s => (
                                <Option key={s.id} value={s.id}>
                                    {`${s.code}${s.name ? ` · ${s.name}` : ''}`}
                                </Option>
                            ))}
                        </Select>
                    </Col>

                    <Col xs={24} md={12}>
                        <label>Route</label>
                        <Row gutter={8} align="middle">
                            <Col flex="auto">
                                <Select
                                    style={{ width: '100%' }}
                                    showSearch
                                    placeholder="From (IATA / city)"
                                    filterOption={false}
                                    onSearch={searchFrom}
                                    value={fromCode}
                                    onChange={setFromCode}
                                    notFoundContent={null}
                                >
                                    {fromOpts.map(a => (
                                        <Option key={a.iata_code} value={a.iata_code}>
                                            {`${a.iata_code} · ${a.name} (${a.city || ''} ${a.country_code || ''})`}
                                        </Option>
                                    ))}
                                </Select>
                            </Col>
                            <Col flex="26px" style={{ textAlign: 'center' }}>
                                <SwapRightOutlined />
                            </Col>
                            <Col flex="auto">
                                <Select
                                    style={{ width: '100%' }}
                                    showSearch
                                    placeholder="To (IATA / city)"
                                    filterOption={false}
                                    onSearch={searchTo}
                                    value={toCode}
                                    onChange={setToCode}
                                    notFoundContent={null}
                                >
                                    {toOpts.map(a => (
                                        <Option key={a.iata_code} value={a.iata_code}>
                                            {`${a.iata_code} · ${a.name} (${a.city || ''} ${a.country_code || ''})`}
                                        </Option>
                                    ))}
                                </Select>
                            </Col>
                        </Row>
                        <div style={{ marginTop: 8 }}>
                            <Radio.Group
                                value={roundTrip ? 'round' : 'oneway'}
                                onChange={e => setRoundTrip(e.target.value === 'round')}
                                optionType="button"
                                buttonStyle="solid"
                            >
                                <Radio.Button value="oneway">One-way</Radio.Button>
                                <Radio.Button value="round">Round-trip</Radio.Button>
                            </Radio.Group>
                        </div>
                    </Col>

                    <Col xs={24} md={12}>
                        <Row gutter={8}>
                            <Col span={roundTrip ? 12 : 24}>
                                <label>Departure</label>
                                <DatePicker
                                    style={{ width: '100%' }}
                                    value={departDate}
                                    onChange={setDepartDate}
                                    disabledDate={d => d && d < moment().startOf('day')}
                                />
                            </Col>
                            {roundTrip && (
                                <Col span={12}>
                                    <label>Return</label>
                                    <DatePicker
                                        style={{ width: '100%' }}
                                        value={returnDate}
                                        onChange={setReturnDate}
                                        disabledDate={d => d && d < (departDate || moment())}
                                    />
                                </Col>
                            )}
                        </Row>
                        <Row gutter={8} style={{ marginTop: 8 }}>
                            <Col span={4}>
                                <label>Adults</label>
                                <InputNumber
                                    min={1}
                                    max={9}
                                    style={{ width: '100%' }}
                                    value={adults}
                                    onChange={setAdults}
                                />
                            </Col>
                            <Col span={4}>
                                <label>Child</label>
                                <InputNumber
                                    min={0}
                                    max={9}
                                    style={{ width: '100%' }}
                                    value={children}
                                    onChange={setChildren}
                                />
                            </Col>
                            <Col span={4}>
                                <label>Infant</label>
                                <InputNumber
                                    min={0}
                                    max={adults}
                                    style={{ width: '100%' }}
                                    value={infants}
                                    onChange={setInfants}
                                />
                            </Col>
                            <Col span={6}>
                                <label>Cabin</label>
                                <Select
                                    style={{ width: '100%' }}
                                    value={cabin}
                                    onChange={setCabin}
                                >
                                    {CABINS.map(c => (
                                        <Option key={c} value={c}>
                                            {c}
                                        </Option>
                                    ))}
                                </Select>
                            </Col>
                            <Col span={6}>
                                <label>Currency / wait</label>
                                <Input.Group compact>
                                    <Input
                                        style={{ width: '45%' }}
                                        placeholder="cur"
                                        maxLength={3}
                                        value={currency}
                                        onChange={e => setCurrency(e.target.value.toUpperCase())}
                                    />
                                    <InputNumber
                                        style={{ width: '55%' }}
                                        min={1000}
                                        max={120000}
                                        step={5000}
                                        value={waitTime}
                                        onChange={setWaitTime}
                                    />
                                </Input.Group>
                            </Col>
                        </Row>
                    </Col>
                </Row>

                <Divider style={{ margin: '12px 0' }} />
                <Button type="primary" icon={<SearchOutlined />} loading={running} onClick={run}>
                    Run search
                </Button>
            </Card>

            {(running || result) && (
                <Card size="small" style={{ marginTop: 16 }} title="Result">
                    <Progress
                        percent={progress}
                        status={running ? 'active' : progress >= 100 ? 'success' : 'normal'}
                    />
                    {result && (
                        <>
                            <div style={{ margin: '8px 0' }}>
                                <Space split={<Divider type="vertical" />}>
                                    <Text>
                                        Flights: <Text strong>{flights.length}</Text>
                                    </Text>
                                    <Text>
                                        progress:{' '}
                                        <Text strong>
                                            {Math.round((result.progress || 0) * 100)}%
                                        </Text>
                                    </Text>
                                    {result.request_id && (
                                        <Text copyable={{ text: result.request_id }}>
                                            request_id: <Text code>{result.request_id}</Text>
                                        </Text>
                                    )}
                                    {result.attempts && <Text>polls: {result.attempts}</Text>}
                                </Space>
                            </div>
                            {flights.length > 0 && <SupplierPriceChart flights={flights} />}
                            <Table
                                size="small"
                                rowKey={(f, i) => `${f.flight_id || i}-${i}`}
                                columns={columns}
                                dataSource={flights}
                                pagination={{ pageSize: 20, showSizeChanger: true }}
                                scroll={{ x: true }}
                                expandable={{
                                    expandedRowRender: f => (
                                        <pre style={{ maxHeight: 300, overflow: 'auto', margin: 0 }}>
                                            {JSON.stringify(f, null, 2)}
                                        </pre>
                                    )
                                }}
                            />
                            <Collapse ghost style={{ marginTop: 8 }}>
                                <Panel header="Request payload" key="req">
                                    <pre style={{ maxHeight: 260, overflow: 'auto' }}>
                                        {JSON.stringify(builtReq, null, 2)}
                                    </pre>
                                </Panel>
                                <Panel header="curl" key="curl">
                                    <Typography.Paragraph
                                        copyable={{ text: curl }}
                                        style={{ marginBottom: 4 }}
                                    >
                                        <Text type="secondary">Copy curl</Text>
                                    </Typography.Paragraph>
                                    <pre style={{ maxHeight: 220, overflow: 'auto' }}>{curl}</pre>
                                </Panel>
                            </Collapse>
                        </>
                    )}
                </Card>
            )}
        </div>
    )
}
